import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Modal, Form, Table, Badge, Alert, Spinner, InputGroup, Tabs, Tab } from 'react-bootstrap';
import { FaBuilding, FaPlus, FaTrash, FaEdit, FaEye, FaSearch, FaToggleOn, FaToggleOff, FaChartBar } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import clientService from '../services/clientService';

const ClientManagement = () => {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    companyName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    country: 'Estonia',
    contactPerson: '',
    notes: '',
    isActive: true
  });

  useEffect(() => {
    fetchClients();
    fetchStatistics();
  }, [showActiveOnly]);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const data = await clientService.getClients(showActiveOnly);
      setClients(data);
    } catch (error) {
      toast.error('Failed to fetch clients');
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      setStatsLoading(true);
      const data = await clientService.getClientStatistics();
      setStatistics(data);
    } catch (error) {
      toast.error('Failed to fetch client statistics');
      console.error('Error fetching statistics:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error('Client name is required');
      return;
    }

    try {
      await clientService.createClient(formData);
      toast.success('Client created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchClients();
      fetchStatistics();
    } catch (error) {
      toast.error(error.message || 'Failed to create client');
    }
  };

  const handleUpdateClient = async (e) => {
    e.preventDefault();
    
    if (!selectedClient) return;

    try {
      await clientService.updateClient(selectedClient.id, formData);
      toast.success('Client updated successfully');
      setShowEditModal(false);
      setSelectedClient(null);
      resetForm();
      fetchClients();
      fetchStatistics();
    } catch (error) {
      toast.error(error.message || 'Failed to update client');
    }
  };

  const handleDeleteClient = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete client "${name}"?`)) {
      try {
        await clientService.deleteClient(id);
        toast.success('Client deleted successfully');
        fetchClients();
        fetchStatistics();
      } catch (error) {
        toast.error(error.message || 'Failed to delete client');
      }
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      await clientService.toggleClientStatus(id, !currentStatus);
      toast.success(`Client ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
      fetchClients();
      fetchStatistics();
    } catch (error) {
      toast.error(error.message || 'Failed to update client status');
    }
  };

  const handleViewClient = async (client) => {
    try {
      const clientDetails = await clientService.getClient(client.id);
      setSelectedClient(clientDetails);
      setShowViewModal(true);
    } catch (error) {
      toast.error('Failed to fetch client details');
    }
  };

  const handleEditClient = (client) => {
    setSelectedClient(client);
    setFormData({
      name: client.name || '',
      companyName: client.company_name || '',
      email: client.email || '',
      phone: client.phone || '',
      address: client.address || '',
      city: client.city || '',
      postalCode: client.postal_code || '',
      country: client.country || 'Estonia',
      contactPerson: client.contact_person || '',
      notes: client.notes || '',
      isActive: client.is_active
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      companyName: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      postalCode: '',
      country: 'Estonia',
      contactPerson: '',
      notes: '',
      isActive: true
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      fetchClients();
      return;
    }

    try {
      setLoading(true);
      const data = await clientService.searchClients(searchTerm);
      setClients(data);
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = clients.filter(client => 
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (client.company_name && client.company_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (client.email && client.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading && clients.length === 0) {
    return (
      <Container>
        <div className="text-center my-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2><FaBuilding className="me-2" />Client Management</h2>
          <p className="text-muted">Manage your clients and their information</p>
        </div>
        <Button 
          variant="primary" 
          onClick={() => setShowCreateModal(true)}
        >
          <FaPlus className="me-1" /> Add New Client
        </Button>
      </div>

      <Tabs defaultActiveKey="clients" className="mb-4">
        <Tab eventKey="clients" title={<><FaBuilding className="me-1" />Clients ({filteredClients.length})</>}>
          <Card>
            <Card.Header>
              <Row className="align-items-center">
                <Col md={6}>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      placeholder="Search clients..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    />
                    <Button variant="outline-secondary" onClick={handleSearch}>
                      <FaSearch />
                    </Button>
                  </InputGroup>
                </Col>
                <Col md={6} className="text-end">
                  <Form.Check
                    type="switch"
                    id="active-only-switch"
                    label="Show active only"
                    checked={showActiveOnly}
                    onChange={(e) => setShowActiveOnly(e.target.checked)}
                    className="d-inline-block"
                  />
                </Col>
              </Row>
            </Card.Header>
            <Card.Body>
              {filteredClients.length === 0 ? (
                <Alert variant="info" className="text-center">
                  {searchTerm ? 'No clients found matching your search.' : 'No clients found. Add your first client to get started.'}
                </Alert>
              ) : (
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Company</th>
                      <th>Contact</th>
                      <th>Location</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredClients.map(client => (
                      <tr key={client.id}>
                        <td>
                          <strong>{client.name}</strong>
                          {client.contact_person && (
                            <div className="text-muted small">
                              Contact: {client.contact_person}
                            </div>
                          )}
                        </td>
                        <td>{client.company_name || '-'}</td>
                        <td>
                          {client.email && (
                            <div>{client.email}</div>
                          )}
                          {client.phone && (
                            <div className="text-muted small">{client.phone}</div>
                          )}
                        </td>
                        <td>
                          {client.city && (
                            <div>{client.city}</div>
                          )}
                          {client.country && client.country !== 'Estonia' && (
                            <div className="text-muted small">{client.country}</div>
                          )}
                        </td>
                        <td>
                          <Badge bg={client.is_active ? 'success' : 'secondary'}>
                            {client.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td>
                          <Button
                            variant="outline-info"
                            size="sm"
                            className="me-1"
                            onClick={() => handleViewClient(client)}
                          >
                            <FaEye />
                          </Button>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-1"
                            onClick={() => handleEditClient(client)}
                          >
                            <FaEdit />
                          </Button>
                          <Button
                            variant={client.is_active ? 'outline-warning' : 'outline-success'}
                            size="sm"
                            className="me-1"
                            onClick={() => handleToggleStatus(client.id, client.is_active)}
                          >
                            {client.is_active ? <FaToggleOff /> : <FaToggleOn />}
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteClient(client.id, client.name)}
                          >
                            <FaTrash />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="statistics" title={<><FaChartBar className="me-1" />Statistics</>}>
          {statsLoading ? (
            <div className="text-center my-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading statistics...</span>
              </Spinner>
            </div>
          ) : statistics ? (
            <>
              <Row className="mb-4">
                <Col md={4}>
                  <Card className="text-center">
                    <Card.Body>
                      <h3 className="text-primary">{statistics.overview.totalClients}</h3>
                      <p className="mb-0">Total Clients</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="text-center">
                    <Card.Body>
                      <h3 className="text-success">{statistics.overview.activeClients}</h3>
                      <p className="mb-0">Active Clients</p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="text-center">
                    <Card.Body>
                      <h3 className="text-secondary">{statistics.overview.inactiveClients}</h3>
                      <p className="mb-0">Inactive Clients</p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Card>
                <Card.Header>
                  <h5>Top Clients by Project Count</h5>
                </Card.Header>
                <Card.Body>
                  <Table size="sm">
                    <thead>
                      <tr>
                        <th>Client</th>
                        <th>Total Projects</th>
                        <th>Active Projects</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statistics.topClients.map(client => (
                        <tr key={client.id}>
                          <td>{client.name}</td>
                          <td>{client.projectCount}</td>
                          <td>{client.activeProjects}</td>
                          <td>
                            <Badge bg={client.is_active ? 'success' : 'secondary'}>
                              {client.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </>
          ) : (
            <Alert variant="warning">Failed to load statistics</Alert>
          )}
        </Tab>
      </Tabs>

      {/* Create Client Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Add New Client</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleCreateClient}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Client Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter client name"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Company Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    placeholder="Enter company name"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter email"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Enter phone number"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Address</Form.Label>
              <Form.Control
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="Enter address"
              />
            </Form.Group>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="Enter city"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Postal Code</Form.Label>
                  <Form.Control
                    type="text"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleInputChange}
                    placeholder="Enter postal code"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Country</Form.Label>
                  <Form.Control
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    placeholder="Enter country"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Contact Person</Form.Label>
              <Form.Control
                type="text"
                name="contactPerson"
                value={formData.contactPerson}
                onChange={handleInputChange}
                placeholder="Enter contact person name"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Enter any additional notes"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleInputChange}
                label="Active client"
              />
            </Form.Group>

            <div className="d-flex justify-content-end">
              <Button 
                variant="secondary" 
                onClick={() => setShowCreateModal(false)}
                className="me-2"
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Create Client
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Edit Client Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Client</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleUpdateClient}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Client Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter client name"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Company Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    placeholder="Enter company name"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter email"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Enter phone number"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Address</Form.Label>
              <Form.Control
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="Enter address"
              />
            </Form.Group>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="Enter city"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Postal Code</Form.Label>
                  <Form.Control
                    type="text"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleInputChange}
                    placeholder="Enter postal code"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Country</Form.Label>
                  <Form.Control
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    placeholder="Enter country"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Contact Person</Form.Label>
              <Form.Control
                type="text"
                name="contactPerson"
                value={formData.contactPerson}
                onChange={handleInputChange}
                placeholder="Enter contact person name"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Enter any additional notes"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleInputChange}
                label="Active client"
              />
            </Form.Group>

            <div className="d-flex justify-content-end">
              <Button 
                variant="secondary" 
                onClick={() => setShowEditModal(false)}
                className="me-2"
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Update Client
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* View Client Modal */}
      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Client Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedClient && (
            <>
              <Row className="mb-4">
                <Col md={6}>
                  <Card>
                    <Card.Body>
                      <h6>Basic Information</h6>
                      <p><strong>Name:</strong> {selectedClient.name}</p>
                      {selectedClient.company_name && (
                        <p><strong>Company:</strong> {selectedClient.company_name}</p>
                      )}
                      {selectedClient.contact_person && (
                        <p><strong>Contact Person:</strong> {selectedClient.contact_person}</p>
                      )}
                      <p><strong>Status:</strong> 
                        <Badge bg={selectedClient.is_active ? 'success' : 'secondary'} className="ms-2">
                          {selectedClient.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card>
                    <Card.Body>
                      <h6>Contact Information</h6>
                      {selectedClient.email && (
                        <p><strong>Email:</strong> {selectedClient.email}</p>
                      )}
                      {selectedClient.phone && (
                        <p><strong>Phone:</strong> {selectedClient.phone}</p>
                      )}
                      {selectedClient.address && (
                        <p><strong>Address:</strong> {selectedClient.address}</p>
                      )}
                      {selectedClient.city && (
                        <p><strong>City:</strong> {selectedClient.city} {selectedClient.postal_code}</p>
                      )}
                      {selectedClient.country && (
                        <p><strong>Country:</strong> {selectedClient.country}</p>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {selectedClient.notes && (
                <Card className="mb-4">
                  <Card.Body>
                    <h6>Notes</h6>
                    <p>{selectedClient.notes}</p>
                  </Card.Body>
                </Card>
              )}

              {selectedClient.projects && selectedClient.projects.length > 0 && (
                <Card>
                  <Card.Header>
                    <h6>Projects ({selectedClient.projects.length})</h6>
                  </Card.Header>
                  <Card.Body>
                    <Table size="sm">
                      <thead>
                        <tr>
                          <th>Project Name</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedClient.projects.map(project => (
                          <tr key={project.id}>
                            <td>{project.name}</td>
                            <td>
                              <Badge bg="primary">{project.status}</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowViewModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ClientManagement;
